import React, { useState, useCallback, useEffect, useContext } from "react";
import Box from "@material-ui/core/Box";
import Alert from "@material-ui/lab/Alert";
import { useLocationGroupsByProductionID } from "../util/db";
import { CircularProgress } from "@material-ui/core";
import LocationGroupListItem from "./LocationGroupListItem";
import { createLocationGroup, updateLocationGroup } from "../util/db";
import { DateContext } from "../providers/DateProvider";
import { CategoryContext } from "../providers/CategoryProvider";
import { DndContext } from "@dnd-kit/core";
import { SortableContext, arrayMove } from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { toast } from "react-hot-toast";
import { useAuth } from "./../util/auth";

// ---- helpers ----
const pad2 = (n) => String(n).padStart(2, "0");

// Always normalize to LOCAL start-of-day first
const startOfLocalDay = (dateLike) => {
  const d = dateLike instanceof Date ? new Date(dateLike) : new Date(dateLike);
  d.setHours(0, 0, 0, 0);
  return d;
};

// Build a dayKey from a Date (assumes it's already local-day-correct)
const toDayKey = (date) => {
  // date MUST be a Date object
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
};

// For legacy ms fields: convert ms → local dayKey safely
const dayKeyFromMs = (ms) => toDayKey(startOfLocalDay(new Date(ms)));

function LocationGroupList(props) {
  const auth = useAuth();

  // NEW provider API (assuming you have dayKey in provider value)
  const { selectedDate, dayKey: selectedDayKey } = useContext(DateContext);
  const { category, setCategory } = useContext(CategoryContext);

  const {
    data: groups,
    status: groupsStatus,
    error: groupsError,
  } = useLocationGroupsByProductionID(props.productionId);

  const [hover, setHover] = React.useState(false);
  const groupsAreEmpty = !groups || groups?.length === 0;
  const [filteredGroups, setFilteredGroups] = useState([]);
  const [listOfGroupsAndDays, setListOfGroupsAndDays] = useState(
    groups?.filter((group) => group?.category?.includes(category))
  );

  const filterGroups = useCallback(() => {
    // Decide selected dayKey (prefer provider dayKey; fallback compute from selectedDate)
    const dayKey = selectedDayKey ?? toDayKey(startOfLocalDay(selectedDate));

    let filty = [];

    if (category !== "production") {
      filty = (groups ?? [])
        .filter((group) => group?.category?.includes(category))
        .filter((group) => {
          // ✅ NEW: use dayKey range if present
          if (group.startDay && group.endDay) {
            return group.startDay <= dayKey && group.endDay >= dayKey;
          }

          // ⛑️ fallback: legacy ms range
          if (group.startDate != null && group.endDate != null) {
            const gStartDay = dayKeyFromMs(group.startDate);
            const gEndDay = dayKeyFromMs(group.endDate);
            return gStartDay <= dayKey && gEndDay >= dayKey;
          }

          // ⛑️ fallback: legacy "day" string
          if (group.day) {
            return group.day === startOfLocalDay(selectedDate).toDateString();
          }

          return false;
        });
    } else {
      filty = (groups ?? []).filter((group) => group?.category?.includes(category));
    }

    setFilteredGroups(
      filty.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || (a.createdAt?.seconds ?? 0) - (b.createdAt?.seconds ?? 0))
    );
  }, [category, groups, selectedDate, selectedDayKey]);

  useEffect(() => {
    filterGroups();
  }, [filterGroups]);

  useEffect(() => {
    setListOfGroupsAndDays(groups?.filter((group) => group?.category?.includes(category)));
  }, [category, groups]);

  const createNewLocationGroup = () => {
    const colors = [
      "#f44336", "#e91e63", "#9c27b0", "#673ab7", "#3f51b5",
      "#2196f3", "#03a9f4", "#00bcd4", "#009688", "#4caf50",
      "#8bc34a", "#ffc107", "#ff9800", "#ff5722", "#795548", "#607d8b",
    ];
    const randomHex = Math.floor(Math.random() * colors.length);

    // ✅ Canonical selected dayKey
    const dayKey = selectedDayKey ?? toDayKey(startOfLocalDay(selectedDate));

    // ⛑️ optional legacy ms during migration (safe to remove later)
    const dayStartMs = startOfLocalDay(selectedDate).getTime();

    const data = {
      productionId: props.productionId,
      productionName: auth.user.productionName,
      name: `LOCATION GROUP ${filteredGroups?.length + 1}`,
      groupColor: colors[randomHex],
      category: category,
      customTypes: [],
      sortOrder: filteredGroups?.length,

      // ✅ NEW fields
      startDay: dayKey,
      endDay: dayKey,

      // ⛑️ legacy fields during migration (optional)
      startDate: dayStartMs,
      endDate: dayStartMs,
    };

    createLocationGroup(data)
      .then((response) => {
        response.success ? toast.success(response.message) : toast.error(response.message);
      })
      .catch((error) => toast.error(error.message));

    if (category === "production") {
      setCategory("shooting");
    }
  };

  const handleOnDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setFilteredGroups((filteredGroups) => {
        const oldIndex = filteredGroups.findIndex((group) => group.id === active.id);
        const newIndex = filteredGroups.findIndex((group) => group.id === over.id);

        const newSortFilteredGroups = arrayMove(filteredGroups, oldIndex, newIndex);

        newSortFilteredGroups.forEach((group, index) => {
          updateLocationGroup(group.id, { sortOrder: index })
            .then((response) => {
              if (response.success) {
                toast.success("Successfully updated location groups sort order.", { id: "sortorder" });
              } else {
                toast.error("Failed to update order of location groups.");
              }
            })
            .catch(() => toast.error("Failed to update order of location groups."));
        });

        return newSortFilteredGroups;
      });
    }
  };

  const buttonStyle = {
    backgroundColor: "#22273eff",
    color: "#fff",
    fontSize: "0.875rem",
    fontWeight: 500,
    padding: "6px 16px",
    border: "none",
    borderRadius: "4px",
    lineHeight: 1.75,
    minWidth: "64px",
    textTransform: "uppercase",
    cursor: "pointer",
    boxShadow:
      "0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)",
    transition: "background-color 250ms, box-shadow 250ms",
  };
  const buttonHoverStyle = { backgroundColor: "#3b4265" };

  return (
    <>
      {groupsError && (
        <Box mb={3}>
          <Alert severity="error">{groupsError.message}</Alert>
        </Box>
      )}

      {(groupsStatus === "loading" || groupsAreEmpty) && (
        <Box py={2} px={3} align="center">
          {groupsStatus === "loading" && <CircularProgress size={32} />}
          {groupsStatus !== "loading" && groupsStatus && <>No location groups created yet.</>}
        </Box>
      )}

      {groupsStatus !== "loading" && groups && groups?.length > 0 && filteredGroups && (
        <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={handleOnDragEnd}>
          <SortableContext items={filteredGroups}>
            {filteredGroups?.map((group, index) => (
              <div key={group.id ?? index}>
                <LocationGroupListItem
                  numberOfGroupsInList={filteredGroups?.length}
                  sortOrder={group.sortOrder}
                  groupId={group.id}
                  groupName={group.name}
                  groupColor={group.groupColor}
                  specialInstructions={group.specialInstructions}
                  lostCallNumber={group.lostCallNumber}
                  lostCallName={group.lostCallName}
                  websiteLinkText={group.websiteLinkText}
                  websiteUrl={group.websiteUrl}
                  groupCustomTypes={group.customTypes}
                  productionId={props.productionId}
                  productionName={auth.user.productionName}
                  listOfGroupsAndDays={listOfGroupsAndDays}

                  // ✅ pass both new and old fields during migration
                  startDay={group.startDay}
                  endDay={group.endDay}
                  startDate={group.startDate}
                  endDate={group.endDate}
                  day={group.day}

                  auth={auth}
                />
                <br />
              </div>
            ))}
          </SortableContext>
        </DndContext>
      )}

      {groupsStatus !== "loading" && groups && groups?.length > 0 && !filteredGroups?.length && (
        <Box py={2} px={3} align="center">
          <>No location groups created yet.</>
        </Box>
      )}

      {auth.user.planIsActive && (
        <button
          onClick={createNewLocationGroup}
          style={{ ...buttonStyle, ...(hover ? buttonHoverStyle : {}), fontWeight: 800 }}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
        >
          + Locations Group
        </button>
      )}
    </>
  );
}

export default LocationGroupList;